import React from 'react';
import { Button, Drawer } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { buildChatThemeCssVars } from '../theme/themeCssVars.js';
import { CHAT_UI_THEME_TOKENS, DEFAULT_CHAT_UI_THEME } from '../theme/themeTokens.js';
import CollapsedSidebarRail from './CollapsedSidebarRail.jsx';
import FlareLogoMark from './FlareLogoMark.jsx';
import SessionListProjectSection from './SessionListProjectSection.jsx';
import { getProjectId } from './sessionIdentity.js';
const SESSION_PANE_DEFAULTS = Object.freeze({
  title: '项目', projectHasMore: false, projectLoading: false, projectLoadingMore: false,
  projectContinuationKey: '',
  collectionScopeKey: 'workspace',
  projectItems: [], projectSlot: null, operations: [], logoSlot: null, sidebarHeaderActions: null,
  footerSlot: null,
  createSessionInProjectLabel: '新会话',
  surfaceBackground: null,
  themeTokens: CHAT_UI_THEME_TOKENS[DEFAULT_CHAT_UI_THEME],
  compact: false, collapsed: false, mobileSidebar: false, showHeader: true,
});
function conditionalValue(condition, whenTrue, whenFalse = null) {
  return condition ? whenTrue : whenFalse;
}
function resolveSurfaceBackground(surfaceBackground, themeTokens) {
  return surfaceBackground ?? themeTokens.sidebarBg;
}
function normalizePaneDisplayProps({ collapsed = false, mobileSidebar = false, ...props }) {
  return { ...props, collapsed, mobileSidebar };
}
/** Render the Session sidebar and optional Project navigation. */
export default function SessionListPane(props) {
  const {
  title, operationsTitle,
  projectHasMore,
  projectContinuationKey,
  collectionScopeKey,
  projectLoading,
  projectLoadingMore,
  onLoadMoreProjects,
  onArchiveProject,
  onPinProject,
  onCreateSession,
  projectItems,
  projectSlot,
  onProjectSelect,
  onSelectSession, sidebarNavigation, activeSessionId, loadingSessionId, runningSessionIds,
  onRenameSession, onArchiveSession, onPinSession,
  createSessionInProjectLabel,
  operations,
  onOperationClick,
  logoSlot,
  sidebarHeaderActions,
  footerSlot,
  themeTokens,
  compact,
  collapsed,
  surfaceBackground,
  showHeader,
  } = { ...SESSION_PANE_DEFAULTS, ...normalizePaneDisplayProps(props) };
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [scrollElement, setScrollElement] = React.useState(null);
  const visibleProjectItems = Array.isArray(projectItems) ? projectItems : [];
  const hasOperations = [Array.isArray(operations), operations.length > 0].every(Boolean);
  const activeProjectId = getProjectId(projectSlot);
  const resolvedSurfaceBackground = resolveSurfaceBackground(surfaceBackground, themeTokens);
  const themeCssVars = buildChatThemeCssVars(themeTokens);
  const resolvedLogoSlot = [logoSlot, <FlareLogoMark key="default-logo" themeTokens={themeTokens} />].find(Boolean);
  const mobileDrawerTitle = (
    <div className="flare-chat-mobile-session-drawer-brand">
      {resolvedLogoSlot}
    </div>
  );
  const mobileDrawerExtra = conditionalValue(Boolean(sidebarHeaderActions), (
    <div className="flare-chat-mobile-session-drawer-actions">
      {sidebarHeaderActions}
    </div>
  ));
  React.useEffect(() => {
    if (!compact) {
      setDrawerOpen(false);
    }
  }, [compact]);
  const closeDrawerInCompactMode = () => {
    if (compact) {
      setDrawerOpen(false);
    }
  };
  const resetSidebarShortcut = sidebarNavigation?.reset;
  React.useEffect(() => {
    const resetOutsideProject = (event) => {
      if (!event.target?.closest?.('[data-project-navigation-row]')) resetSidebarShortcut?.();
    };
    document.addEventListener('pointerdown', resetOutsideProject, true);
    document.addEventListener('keydown', resetOutsideProject, true);
    return () => {
      document.removeEventListener('pointerdown', resetOutsideProject, true);
      document.removeEventListener('keydown', resetOutsideProject, true);
    };
  }, [resetSidebarShortcut]);
  const handleProjectSelect = (project = projectSlot) => {
    onProjectSelect?.(project);
    closeDrawerInCompactMode();
  };
  const handleOperationClick = (operation) => {
    sidebarNavigation?.reset();
    onOperationClick?.(operation);
    closeDrawerInCompactMode();
  };
  const renderCollapsedPaneContent = () => (
    <CollapsedSidebarRail
      activeProjectId={activeProjectId}
      logoSlot={logoSlot}
      onOperationClick={handleOperationClick}
      operations={operations}
      sidebarHeaderActions={sidebarHeaderActions}
      background={resolvedSurfaceBackground}
      themeTokens={themeTokens}
    />
  );
  const renderSectionTitle = label => conditionalValue(Boolean(label), (
    <div className="flare-sidebar-section-title" role="heading" aria-level={2} style={{ color: themeTokens.textMuted }}>
      {label}
    </div>
  ));
  const renderPaneContent = (paneCompact, paneShowHeader) => (
    <div
      style={{
        ...themeCssVars,
        background: themeTokens.sidebarBg,
        backgroundColor: resolvedSurfaceBackground,
        borderBottom: conditionalValue(paneCompact, `1px solid ${themeTokens.divider}`, 0),
        borderRight: conditionalValue(paneCompact || resolvedSurfaceBackground === 'transparent', 0, `1px solid ${themeTokens.divider}`),
        display: 'flex',
        flexDirection: 'column',
        height: conditionalValue(paneCompact, 'auto', '100%'),
        maxHeight: conditionalValue(paneCompact, '48vh', 'none'),
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {conditionalValue(paneShowHeader, (
        <div
          className="flare-sidebar-logo-slot"
          style={{
            alignItems: 'center',
            display: 'flex',
            gap: 8,
            justifyContent: 'space-between',
            minHeight: 44,
            padding: conditionalValue(paneCompact, '10px 12px 6px', '12px 14px 8px'),
          }}
        >
          <div style={{ minWidth: 0 }}>
            {resolvedLogoSlot}
          </div>
          {conditionalValue(Boolean(sidebarHeaderActions), (
            <div
              className="flare-sidebar-header-actions"
              style={{ alignItems: 'center', display: 'inline-flex', flex: '0 0 auto' }}
            >
              {sidebarHeaderActions}
            </div>
          ))}
        </div>
      ))}
      {conditionalValue(hasOperations, (
        <div className="flare-sidebar-primary-navigation" style={{ padding: conditionalValue(paneCompact, '0 10px 8px', '0 12px 8px') }}>
          {renderSectionTitle(operationsTitle)}
          {operations.map((operation) => (
            <Button
              block
              key={[operation.key, operation.label].find(Boolean)}
              icon={operation.icon}
              onClick={() => handleOperationClick(operation)}
              style={{ color: themeTokens.textPrimary, justifyContent: 'flex-start', textAlign: 'left' }}
              type="text"
            >
              {operation.label}
            </Button>
          ))}
        </div>
      ))}
      <div style={{ padding: paneCompact ? '0 10px' : '0 12px' }}>
        {renderSectionTitle(title)}
      </div>
      <div
        className={compact || paneCompact ? 'flare-mobile-session-scroll-owner' : undefined}
        data-collection-scroll-owner="sidebar"
        ref={setScrollElement}
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          padding: paneCompact ? 'var(--flare-scroll-edge-buffer, 12px) 10px' : 'var(--flare-scroll-edge-buffer, 12px) 12px',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <SessionListProjectSection
          sidebarNavigation={sidebarNavigation}
          activeSessionId={activeSessionId}
          loadingSessionId={loadingSessionId}
          runningSessionIds={runningSessionIds}
          onSelectSession={async (sessionId, projectId) => {
            if (!await onSelectSession?.(sessionId, projectId)) return;
            closeDrawerInCompactMode();
          }}
          onRenameSession={onRenameSession}
          onArchiveSession={onArchiveSession}
          onPinSession={onPinSession}
          activeProjectId={activeProjectId}
          createSessionInProjectLabel={createSessionInProjectLabel}
          onArchiveProject={onArchiveProject}
          onPinProject={onPinProject}
          onCreateSession={onCreateSession}
          onLoadMoreProjects={onLoadMoreProjects}
          onProjectSelect={handleProjectSelect}
          paneCompact={paneCompact}
          projectContinuationKey={projectContinuationKey}
          collectionScopeKey={collectionScopeKey}
          projectError={props.error}
          projectHasMore={projectHasMore}
          projectItems={visibleProjectItems}
          projectLoading={projectLoading}
          projectLoadingMore={projectLoadingMore}
          projectSlot={projectSlot}
          scrollElement={scrollElement}
          themeTokens={themeTokens}
        />
      </div>
      {conditionalValue(Boolean(footerSlot), (
        <div className="flare-sidebar-footer-slot">
          {footerSlot}
        </div>
      ))}
    </div>
  );
  if (collapsed && !compact) {
    return renderCollapsedPaneContent();
  }
  if (!compact) {
    return renderPaneContent(false, showHeader);
  }
  return (
    <>
      <div
        style={{
          alignItems: 'center',
          background: themeTokens.sidebarBg,
          borderBottom: `1px solid ${themeTokens.divider}`,
          display: 'flex',
          justifyContent: 'flex-start',
          padding: '8px 10px',
        }}
      >
        <Button
          icon={<MenuOutlined />}
          onClick={() => setDrawerOpen(true)}
          style={{ color: themeTokens.textPrimary }}
          type="text"
          aria-label={title || '打开侧栏'}
        >
          {title || null}
        </Button>
      </div>
      <Drawer
        extra={mobileDrawerExtra}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        placement="left"
        rootClassName="flare-chat-mobile-session-drawer-root"
        styles={{
          body: { ...themeCssVars, padding: 0 },
          wrapper: { ...themeCssVars, width: '86vw' },
        }}
        title={mobileDrawerTitle}
      >
        {renderPaneContent(false, false)}
      </Drawer>
    </>
  );
}
